import clsx from "clsx";
import React, { useRef, useState } from "react";

import {
  FileFieldDeleteIcon,
  FileFieldDocumentIcon,
  FileFieldImagePlaceholderIcon,
  FileFieldRefreshIcon,
  FileFieldUploadIcon,
} from "./icons";
import { TFileFieldProps } from "./types";

import s from "./FileField.less";

const defaultHint = "(PDF, JPG или PNG до 5 MB)";

const FileField = ({
  label,
  value,
  variant = "document",
  accept,
  maxSizeMb,
  hint = defaultHint,
  uploadLabel = "Загрузить файл",
  disabled = false,
  readOnly = false,
  loading = false,
  error,
  templateLink,
  className,
  icons,
  previewSlot,
  onUpload,
  onDelete,
  onDownload,
}: TFileFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const UploadIcon = icons?.Upload ?? FileFieldUploadIcon;
  const DeleteIcon = icons?.Delete ?? FileFieldDeleteIcon;
  const DocumentIcon = icons?.Document ?? FileFieldDocumentIcon;
  const ImagePlaceholderIcon =
    icons?.ImagePlaceholder ?? FileFieldImagePlaceholderIcon;

  const canInteract = !disabled && !readOnly && !loading;

  const openPicker = () => {
    if (!canInteract || !onUpload) return;
    inputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file || !onUpload) return;

    setLocalError(null);

    if (maxSizeMb && file.size > maxSizeMb * 1024 * 1024) {
      setLocalError(`Файл не должен превышать ${maxSizeMb} MB`);
      return;
    }

    await onUpload(file);
  };

  const handleDownload = () => {
    if (!value) return;
    if (onDownload) {
      onDownload(value);
      return;
    }
    window.open(value.link, "_blank", "noopener,noreferrer");
  };

  const displayError = error || localError;
  const hasFile = Boolean(value?.id || value?.link);

  if (variant === "photo") {
    return (
      <div className={clsx(s.root, s.photoRoot, className)}>
        {label && <span className={s.label}>{label}</span>}

        {hasFile ? (
          <>
            <div className={s.photoHeader}>
              <button
                type="button"
                className={s.fileName}
                onClick={handleDownload}
                disabled={!value?.link}
              >
                {value?.name}
              </button>
              <button
                type="button"
                className={s.iconAction}
                onClick={onDelete}
                disabled={!canInteract}
                aria-label="Удалить файл"
              >
                <DeleteIcon size={12} />
              </button>
            </div>

            <div className={s.photoPreview}>
              {previewSlot ?? (
                <img
                  src={value?.link}
                  alt={value?.name}
                  className={s.photoImage}
                />
              )}
            </div>
          </>
        ) : (
          <>
            <button
              type="button"
              className={s.photoUploadArea}
              onClick={openPicker}
              disabled={!canInteract}
            >
              <UploadIcon size={16} className={s.photoUploadIcon} />
              <div className={s.uploadText}>
                <span className={s.photoUploadTitle}>{uploadLabel}</span>
                {hint && <span className={s.uploadHint}>{hint}</span>}
              </div>
            </button>

            <div className={s.photoPreview}>
              <ImagePlaceholderIcon
                size={20}
                className={s.photoPlaceholderIcon}
              />
            </div>
          </>
        )}

        <input
          ref={inputRef}
          type="file"
          className={s.hiddenInput}
          accept={accept}
          disabled={!canInteract}
          onChange={handleFileChange}
        />

        {displayError && <span className={s.error}>{displayError}</span>}
      </div>
    );
  }

  return (
    <div className={clsx(s.root, className)}>
      {label && <span className={s.label}>{label}</span>}

      <div className={s.body}>
        {hasFile ? (
          <div className={s.fileRow}>
            <div className={s.iconBox}>
              <DocumentIcon size={24} />
            </div>
            <div className={s.fileMeta}>
              <div className={s.fileNameRow}>
                <button
                  type="button"
                  className={s.fileName}
                  onClick={handleDownload}
                  disabled={!value?.link}
                  title="Открыть файл"
                >
                  {value?.name}
                </button>
                <button
                  type="button"
                  className={s.iconAction}
                  onClick={openPicker}
                  disabled={!canInteract}
                  aria-label="Заменить файл"
                  title="Заменить файл"
                >
                  <FileFieldRefreshIcon size={12} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={clsx(s.uploadArea, readOnly && s.uploadAreaReadOnly)}
            onClick={openPicker}
            disabled={!canInteract}
          >
            <div className={s.iconBox}>
              <UploadIcon size={16} />
            </div>
            <div className={s.uploadText}>
              <span className={s.uploadTitle}>{uploadLabel}</span>
              {hint && <span className={s.uploadHint}>{hint}</span>}
            </div>
          </button>
        )}

        {templateLink && (
          <a
            href={templateLink.href}
            className={s.templateLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {templateLink.label}
          </a>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        className={s.hiddenInput}
        accept={accept}
        disabled={!canInteract}
        onChange={handleFileChange}
      />

      {displayError && <span className={s.error}>{displayError}</span>}
    </div>
  );
};

export default FileField;
